export type GateDecision =
  | { state: 'checkout'; offerSlug: string }
  | { state: 'allowed' }
  | { state: 'manual' };

export type VerifiedGateState =
  | 'allowed'
  | 'pending'
  | 'manual'
  | 'error'
  | 'dismissed';

export type PlandalfSdk = {
  identify?: (token: string) => void;
  ready?: (callback: () => void) => void;
  addGate?: (
    name: string,
    offerSlug: string,
    check: () => boolean | Promise<boolean>
  ) => void;
  gate?: (
    name: string,
    syncBilling: () => Promise<unknown>,
    options: { user: string; mode: string; session: null }
  ) => Promise<{ access?: boolean; status?: string; error?: unknown }>;
  present?: (
    offerSlug: string,
    options: { user?: string; mode?: string; session?: null }
  ) => Promise<{ status?: string }>;
  dispatch?: (event: string) => void;
  isWorkflowActive?: (event: string) => boolean;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (
    event: string,
    handler: (...args: unknown[]) => void
  ) => void;
};

export type PreparedGate = {
  decision: GateDecision;
  customerJwt?: string | null;
};

export async function runVerifiedGate({
  sdk,
  name,
  decision,
  customerJwt,
  syncBilling,
  readAccess,
  mode = 'preview',
}: {
  sdk: PlandalfSdk;
  name: string;
  decision: GateDecision;
  customerJwt?: string | null;
  syncBilling: () => Promise<unknown>;
  readAccess: () => boolean | Promise<boolean>;
  mode?: string;
}): Promise<{ state: VerifiedGateState }> {
  if (decision.state === 'manual') return decision;

  const accessBefore = await readAccess();
  if (accessBefore === true) return { state: 'allowed' };
  if (decision.state !== 'checkout') return { state: 'pending' };
  if (!customerJwt) throw new Error('Signed customer identity is required');
  if (typeof sdk.addGate !== 'function' || typeof sdk.gate !== 'function') {
    return { state: 'manual' };
  }

  if (mode !== 'live') {
    console.log(
      '[Plandalf] Direct checkout reference URL (SDK URL may differ):',
      `https://bonjoro.plandalf.dev/o/${decision.offerSlug}?mode=preview`
    );
  }
  console.log('[Plandalf] SDK checkout mode:', mode);
  sdk.identify?.(customerJwt);
  sdk.addGate(name, decision.offerSlug, () => readAccess());
  let syncFailed = false;
  const result = await sdk.gate(
    name,
    async () => {
      try {
        await syncBilling();
      } catch {
        syncFailed = true;
      }
    },
    { user: customerJwt, mode, session: null }
  );

  if (result.status === 'error') return { state: 'error' };
  if (syncFailed) return { state: 'pending' };
  if (!result.access) {
    return {
      state: result.status === 'purchased' ? 'pending' : 'dismissed',
    };
  }
  return { state: (await readAccess()) === true ? 'allowed' : 'pending' };
}
